import React, { useMemo } from 'react';
import { generatePath, Link } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Spinner,
} from '@/Components/shadcn';
import useCompanyId from '@/Hooks/UseCompanyId';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';
import { cn } from '@/lib/utils';
import EmailCandidateFormContainer from './EmailCandidateFormContainer';
import { useOutreachEmailTemplate } from '@/Pages/Candidates/AiSearch/api/useOutreachCandidate';
import { useEmailIntegrationStatus } from '@/Pages/AccountSettings/AccountSettingsTabs/IntegrationsTab/api/useOAuthEmailIntegration';
import { useOutreachCandidatePopoutContext } from '../OutreachContext';

const EmailCandidateDialog = () => {
    const { openSendEmail, closeOutreachPopout, selectedProfile } = useOutreachCandidatePopoutContext();
    const isSingleProfile = selectedProfile.length === 1;
    const { data, isLoading: isLoadingEmailIntegrationStatus } = useEmailIntegrationStatus();
    const { isLoading: isLoadingEmailTemplate } = useOutreachEmailTemplate();
    const isLoading = isLoadingEmailTemplate || isLoadingEmailIntegrationStatus;
    const companyId = useCompanyId();
    const maxWidthClass = useMemo(() => {
        if (data?.connectionStatus !== 'Valid') {
            return 'max-w-md';
        }
        if (isSingleProfile) {
            return 'max-w-5xl';
        }
        return 'max-w-6xl';
    }, [data?.connectionStatus, isSingleProfile]);

    return (
        <Dialog
            open={openSendEmail}
            onOpenChange={(next) => {
                if (!next) closeOutreachPopout();
            }}
        >
            <DialogContent
                className={cn('max-h-10/12 gap-0 overflow-auto p-0 sm:rounded-lg', maxWidthClass)}
                aria-describedby={undefined}
            >
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Spinner className="text-primary size-10" />
                    </div>
                ) : (
                    <div>
                        {data?.connectionStatus !== 'Valid' ? (
                            <div className="flex flex-col gap-4 p-6">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-semibold">
                                        {UIStrings.ConnectYourWorkEmail}
                                    </DialogTitle>
                                    <DialogDescription className="text-base">
                                        {UIStrings.ToSendEmailsDirectlyFromTalentMesh}
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogFooter className="gap-2 sm:justify-end">
                                    <Button type="button" variant="outline" onClick={closeOutreachPopout}>
                                        {UIStrings.Cancel}
                                    </Button>
                                    <Button type="button" asChild>
                                        <Link to={generatePath(RoutePath.AccountSettingsIntegrations, { companyId })}>
                                            {UIStrings.GoToSettings}
                                        </Link>
                                    </Button>
                                </DialogFooter>
                            </div>
                        ) : (
                            <div className="flex flex-col">
                                <DialogHeader className="sr-only">
                                    <DialogTitle className="text-2xl font-semibold">
                                        {UIStrings.EmailOutreach}
                                    </DialogTitle>
                                    <DialogDescription className="text-base">
                                        {UIStrings.SendEmailToTheFollowingCandidates}
                                    </DialogDescription>
                                </DialogHeader>
                                <EmailCandidateFormContainer />
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EmailCandidateDialog;
